import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  DriveContentState,
  DriveFileActionEvent,
  DriveFileCard,
  DriveFolderActionEvent,
  DriveFolderCard,
  DriveFolderRef,
  DriveItemType,
  DriveSelectedItem,
  DriveSelectionToolbarEvent,
} from './drive.types';

@Component({
  selector: 'app-resource-drive-content',
  templateUrl: './drive-content.component.html',
  styleUrls: ['./drive-content.component.scss'],
  standalone: false,
})
export class ResourceDriveContentComponent implements OnChanges, OnDestroy {
  selectedFolderIds = new Set<number>();
  selectedFileIds = new Set<number>();
  private selectionAnchor: { type: DriveItemType; id: number } | null = null;
  /** Delay plain-click select so dblclick can cancel before toolbar shifts layout. */
  private pendingSelectTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly SELECT_CLICK_DELAY_MS = 280;
  @Input() state: DriveContentState = 'loading';
  @Input() searchKeyword = '';
  @Input() isDriveSearch = false;
  @Input() folderCards: DriveFolderCard[] = [];
  @Input() fileCards: DriveFileCard[] = [];
  @Input() sortableFileCards: DriveFileCard[] = [];
  @Input() otherFileCards: DriveFileCard[] = [];
  @Input() canSortFiles = false;
  @Input() breadcrumb: { id: number; name: string }[] = [];
  @Input() filesLoadError: string | null = null;
  @Input() totalNum = 0;
  @Input() pageIndex = 1;
  @Input() pageSize = 10;
  @Input() resourceType = '';
  @Input() showCopyAction = false;
  @Input() currentFolderId: number | null = null;
  @Input() selectionResetToken = 0;
  @Input() hasClipboard = false;
  @Input() canPaste = false;

  @Output() pageChange = new EventEmitter<{ pageIndex: number; pageSize: number }>();
  @Output() fileOpen = new EventEmitter<number>();
  @Output() fileAction = new EventEmitter<DriveFileActionEvent>();
  @Output() folderOpen = new EventEmitter<number[]>();
  @Output() folderAction = new EventEmitter<DriveFolderActionEvent>();
  @Output() folderSort = new EventEmitter<CdkDragDrop<DriveFolderCard[]>>();
  @Output() fileSort = new EventEmitter<CdkDragDrop<DriveFileCard[]>>();
  @Output() breadcrumbNavigate = new EventEmitter<number>();
  @Output() retryFiles = new EventEmitter<void>();
  @Output() selectionToolbarAction = new EventEmitter<DriveSelectionToolbarEvent>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectionResetToken'] && !changes['selectionResetToken'].firstChange) {
      this.clearPendingSelect();
      this.clearSelection(false);
    }
  }

  ngOnDestroy(): void {
    this.clearPendingSelect();
  }

  get hasSelection(): boolean {
    return this.selectedFolderIds.size > 0 || this.selectedFileIds.size > 0;
  }

  get selectedCount(): number {
    return this.selectedFolderIds.size + this.selectedFileIds.size;
  }

  get showSelectionToolbar(): boolean {
    return this.hasSelection || this.hasClipboard;
  }

  get canCopySelection(): boolean {
    return this.showCopyAction && this.selectedFileIds.size > 0 && this.selectedFolderIds.size === 0;
  }

  get canCutSelection(): boolean {
    return this.hasSelection;
  }

  get canMoveSelection(): boolean {
    return this.hasSelection;
  }

  onFolderDrop(event: CdkDragDrop<DriveFolderCard[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    this.folderSort.emit(event);
  }

  onFileDrop(event: CdkDragDrop<DriveFileCard[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    this.fileSort.emit(event);
  }

  trackFileById(_index: number, file: DriveFileCard): number {
    return file.id;
  }

  onPageIndexChange(pageIndex: number): void {
    this.pageChange.emit({ pageIndex, pageSize: this.pageSize });
  }

  onPageSizeChange(pageSize: number): void {
    this.pageChange.emit({ pageIndex: 1, pageSize });
  }

  onBreadcrumbClick(index: number): void {
    this.breadcrumbNavigate.emit(index);
  }

  onRootBreadcrumbClick(): void {
    this.breadcrumbNavigate.emit(-1);
  }

  trackFolderById(_index: number, folder: DriveFolderCard): number {
    return folder.id;
  }

  getLocateFolders(file: DriveFileCard): DriveFolderRef[] {
    return file.folders ?? [];
  }

  getFolderLabel(folder: DriveFolderRef): string {
    return folder.folderName ?? `文件夹 ${folder.folderId}`;
  }

  isCurrentLocateFolder(folder: DriveFolderRef): boolean {
    return this.currentFolderId != null && folder.folderId === this.currentFolderId;
  }

  shouldShowFileStatus(file: DriveFileCard): boolean {
    return file.enabled != null && file.enabled !== 1;
  }

  getFileStatusLabel(file: DriveFileCard): string {
    if (file.enabled === 0) {
      return '已禁用';
    }
    if (file.enabled === -1) {
      return '处理中';
    }
    return '';
  }

  canRemoveFromFolder(file: DriveFileCard): boolean {
    if (this.getLocateFolders(file).length <= 1) {
      return false;
    }
    return this.getRemoveFromFolderId(file) != null;
  }

  getRemoveFromFolderId(file: DriveFileCard): number | null {
    const folders = this.getLocateFolders(file);
    if (this.currentFolderId != null) {
      const currentFolder = folders.find((folder) => folder.folderId === this.currentFolderId);
      if (currentFolder) {
        return currentFolder.folderId;
      }
    }
    return folders[0]?.folderId ?? null;
  }

  getPrimaryFolderLabel(file: DriveFileCard): string | null {
    const folders = this.getLocateFolders(file);
    if (!folders.length) {
      return null;
    }
    return this.getFolderLabel(folders[0]);
  }

  isFileEnabled(file: DriveFileCard): boolean {
    return (file.enabled ?? 1) === 1;
  }

  canToggleFileEnabled(file: DriveFileCard): boolean {
    return file.enabled !== -1;
  }

  getFileToggleEnabledLabel(file: DriveFileCard): string {
    if (file.enabled === -1) {
      return '处理中';
    }
    return this.isFileEnabled(file) ? '禁用' : '启用';
  }

  isFolderEnabled(folder: DriveFolderCard): boolean {
    return (folder.enabled ?? 1) === 1;
  }

  getFolderToggleEnabledLabel(folder: DriveFolderCard): string {
    return this.isFolderEnabled(folder) ? '禁用' : '启用';
  }

  shouldShowFolderStatus(folder: DriveFolderCard): boolean {
    return folder.enabled != null && folder.enabled !== 1;
  }

  getFolderStatusLabel(folder: DriveFolderCard): string {
    if (folder.enabled === 0) {
      return '已禁用';
    }
    if (folder.enabled === -1) {
      return '处理中';
    }
    return '';
  }

  emitFolderAction(action: DriveFolderActionEvent['action'], folder: DriveFolderCard): void {
    this.folderAction.emit({ action, folderId: folder.id });
  }

  emitFileAction(
    action: DriveFileActionEvent['action'],
    file: DriveFileCard,
    extra: Partial<DriveFileActionEvent> = {},
  ): void {
    this.fileAction.emit({ action, fileId: file.id, ...extra });
  }

  isFolderSelected(folderId: number): boolean {
    return this.selectedFolderIds.has(folderId);
  }

  isFileSelected(fileId: number): boolean {
    return this.selectedFileIds.has(fileId);
  }

  onFolderCardClick(folder: DriveFolderCard, event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('.drive-file-menu, .drive-drag-handle')) {
      return;
    }
    this.scheduleOrHandleItemClick('folder', folder.id, this.folderCards, event);
  }

  onFileCardClick(file: DriveFileCard, event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('.drive-file-menu, .drive-drag-handle')) {
      return;
    }
    const files = this.canSortFiles ? this.sortableFileCards : this.fileCards;
    this.scheduleOrHandleItemClick('file', file.id, files, event);
  }

  onFolderCardDblClick(folder: DriveFolderCard): void {
    this.clearPendingSelect();
    this.folderOpen.emit(folder.pathIds);
  }

  onFileCardDblClick(file: DriveFileCard): void {
    this.clearPendingSelect();
    this.fileOpen.emit(file.id);
  }

  onContentBackgroundClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('.drive-folder-card, .drive-file-card, .drive-selection-toolbar')) {
      return;
    }
    this.clearPendingSelect();
    this.clearSelection();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.hasSelection) {
      this.clearPendingSelect();
      this.clearSelection();
    }
  }

  emitToolbarAction(action: DriveSelectionToolbarEvent['action']): void {
    this.selectionToolbarAction.emit({
      action,
      items: this.getSelectedItems(),
    });
    if (action === 'copy' || action === 'cut' || action === 'move') {
      this.clearSelection(false);
    }
  }

  private scheduleOrHandleItemClick(
    type: DriveItemType,
    itemId: number,
    items: Array<DriveFolderCard | DriveFileCard>,
    event: MouseEvent,
  ): void {
    // Second click of a double-click — cancel pending select so toolbar doesn't shift cards.
    if (event.detail > 1) {
      this.clearPendingSelect();
      return;
    }
    const isMultiToggle = event.ctrlKey || event.metaKey;
    const isRangeSelect = event.shiftKey;
    if (isMultiToggle || isRangeSelect) {
      this.clearPendingSelect();
      this.handleItemClick(type, itemId, items, event);
      return;
    }
    this.clearPendingSelect();
    this.pendingSelectTimer = setTimeout(() => {
      this.pendingSelectTimer = null;
      this.handleItemClick(type, itemId, items, event);
    }, ResourceDriveContentComponent.SELECT_CLICK_DELAY_MS);
  }

  private clearPendingSelect(): void {
    if (this.pendingSelectTimer != null) {
      clearTimeout(this.pendingSelectTimer);
      this.pendingSelectTimer = null;
    }
  }

  private handleItemClick(
    type: DriveItemType,
    itemId: number,
    items: Array<DriveFolderCard | DriveFileCard>,
    event: MouseEvent,
  ): void {
    const isMultiToggle = event.ctrlKey || event.metaKey;
    const isRangeSelect = event.shiftKey;
    if (isRangeSelect && this.selectionAnchor?.type === type) {
      this.selectRange(type, itemId, items);
    } else if (isMultiToggle) {
      this.toggleItemSelection(type, itemId);
      this.selectionAnchor = { type, id: itemId };
    } else {
      this.selectedFolderIds.clear();
      this.selectedFileIds.clear();
      this.setItemSelected(type, itemId, true);
      this.selectionAnchor = { type, id: itemId };
    }
  }

  private selectRange(
    type: DriveItemType,
    itemId: number,
    items: Array<DriveFolderCard | DriveFileCard>,
  ): void {
    const anchorId = this.selectionAnchor?.id;
    if (anchorId == null) {
      this.setItemSelected(type, itemId, true);
      return;
    }
    const anchorIndex = items.findIndex((item) => item.id === anchorId);
    const targetIndex = items.findIndex((item) => item.id === itemId);
    if (anchorIndex < 0 || targetIndex < 0) {
      this.setItemSelected(type, itemId, true);
      return;
    }
    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);
    if (type === 'folder') {
      this.selectedFolderIds.clear();
      this.selectedFileIds.clear();
      for (let index = start; index <= end; index += 1) {
        this.selectedFolderIds.add(items[index].id);
      }
      return;
    }
    this.selectedFolderIds.clear();
    this.selectedFileIds.clear();
    for (let index = start; index <= end; index += 1) {
      this.selectedFileIds.add(items[index].id);
    }
  }

  private toggleItemSelection(type: DriveItemType, itemId: number): void {
    const selectedSet = type === 'folder' ? this.selectedFolderIds : this.selectedFileIds;
    if (selectedSet.has(itemId)) {
      selectedSet.delete(itemId);
      return;
    }
    selectedSet.add(itemId);
  }

  private setItemSelected(type: DriveItemType, itemId: number, selected: boolean): void {
    const selectedSet = type === 'folder' ? this.selectedFolderIds : this.selectedFileIds;
    if (selected) {
      selectedSet.add(itemId);
      return;
    }
    selectedSet.delete(itemId);
  }

  private getSelectedItems(): DriveSelectedItem[] {
    const folders = this.folderCards
      .filter((folder) => this.selectedFolderIds.has(folder.id))
      .map((folder) => ({ type: 'folder' as const, id: folder.id, name: folder.name }));
    const files = this.fileCards
      .filter((file) => this.selectedFileIds.has(file.id))
      .map((file) => ({ type: 'file' as const, id: file.id, name: file.name }));
    return [...folders, ...files];
  }

  private clearSelection(emitAction = true): void {
    this.selectedFolderIds.clear();
    this.selectedFileIds.clear();
    this.selectionAnchor = null;
    if (emitAction) {
      this.selectionToolbarAction.emit({ action: 'clear', items: [] });
    }
  }
}
